import React from "react";
import styled from "styled-components";

const SkeletonWrapper = styled.div`
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  .skeleton-base {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 1000px 100%;
    animation: shimmer 2s infinite;
    border-radius: 8px;
  }
`;

const SkeletonContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const SkeletonContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const SkeletonGallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SkeletonImageMain = styled.div`
  width: 100%;
  height: 500px;
  border-radius: 8px;
`;

const SkeletonThumbnails = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
`;

const SkeletonThumbnail = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 8px;
  flex-shrink: 0;
`;

const SkeletonDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const SkeletonTitle = styled.div`
  height: 32px;
  width: 80%;
  margin-bottom: 20px;
  border-radius: 8px;
`;

const SkeletonText = styled.div`
  height: 16px;
  width: 100%;
  margin-bottom: 10px;
  border-radius: 8px;

  &:last-child {
    width: 90%;
  }
`;

const SkeletonPrice = styled.div`
  height: 28px;
  width: 150px;
  margin: 20px 0;
  border-radius: 8px;
`;

const SkeletonButton = styled.div`
  height: 48px;
  width: 100%;
  margin: 15px 0;
  border-radius: 4px;
`;

const SkeletonTabs = styled.div`
  display: flex;
  gap: 20px;
  margin: 40px 0;
  border-bottom: 1px solid #e8e8e8;
`;

const SkeletonTab = styled.div`
  height: 24px;
  width: 120px;
  border-radius: 8px;
`;

const SkeletonSection = styled.div`
  margin-bottom: 60px;
`;

const SkeletonReview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SkeletonReviewTitle = styled.div`
  height: 18px;
  width: 200px;
  border-radius: 8px;
`;

const SkeletonReviewText = styled.div`
  height: 16px;
  width: 100%;
  border-radius: 8px;
  margin-bottom: 8px;

  &:last-child {
    width: 90%;
  }
`;

const ProductDetailSkeleton = () => {
  return (
    <SkeletonWrapper>
      <SkeletonContainer>
        {/* Product Content Skeleton */}
        <SkeletonContent>
          {/* Left Gallery */}
          <SkeletonGallery>
            <SkeletonImageMain className="skeleton-base" />
            <SkeletonThumbnails>
              <SkeletonThumbnail className="skeleton-base" />
              <SkeletonThumbnail className="skeleton-base" />
              <SkeletonThumbnail className="skeleton-base" />
            </SkeletonThumbnails>
          </SkeletonGallery>

          {/* Right Details */}
          <SkeletonDetails>
            <SkeletonTitle className="skeleton-base" />
            <SkeletonText className="skeleton-base" />
            <SkeletonText className="skeleton-base" style={{ width: "80%" }} />

            <SkeletonPrice className="skeleton-base" />

            <SkeletonText className="skeleton-base" />
            <SkeletonText className="skeleton-base" style={{ width: "70%" }} />

            <SkeletonButton className="skeleton-base" />
            <SkeletonButton className="skeleton-base" />

            <div style={{ marginTop: "20px" }}>
              <SkeletonText className="skeleton-base" />
              <SkeletonText
                className="skeleton-base"
                style={{ width: "60%" }}
              />
            </div>
          </SkeletonDetails>
        </SkeletonContent>

        {/* Tabs Skeleton */}
        <SkeletonTabs>
          <SkeletonTab className="skeleton-base" />
          <SkeletonTab className="skeleton-base" />
          <SkeletonTab className="skeleton-base" />
        </SkeletonTabs>

        {/* Content Section Skeleton */}
        <SkeletonSection>
          <SkeletonTitle
            className="skeleton-base"
            style={{ marginBottom: "20px" }}
          />
          <SkeletonText className="skeleton-base" />
          <SkeletonText className="skeleton-base" />
          <SkeletonText className="skeleton-base" style={{ width: "85%" }} />
        </SkeletonSection>

        {/* Reviews Section Skeleton */}
        <SkeletonSection>
          <SkeletonTitle
            className="skeleton-base"
            style={{ marginBottom: "20px" }}
          />
          <SkeletonReview>
            <SkeletonReviewTitle className="skeleton-base" />
            <SkeletonReviewText className="skeleton-base" />
            <SkeletonReviewText className="skeleton-base" />
          </SkeletonReview>
          <SkeletonReview>
            <SkeletonReviewTitle className="skeleton-base" />
            <SkeletonReviewText className="skeleton-base" />
            <SkeletonReviewText className="skeleton-base" />
          </SkeletonReview>
        </SkeletonSection>
      </SkeletonContainer>
    </SkeletonWrapper>
  );
};

export default ProductDetailSkeleton;
